import * as DialogPrimitive from "@radix-ui/react-dialog";
import { usePostHog } from "@posthog/react";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useRestoreFocusOnClose } from "../../hooks/useRestoreFocusOnClose";
import {
  advisorContextKindOf,
  seedSuggestionForContext,
} from "../../lib/advisor-context";
import {
  bucketAdvisorDrawerDuration,
  captureAdvisorDrawerClosed,
  captureAdvisorDrawerExpanded,
} from "../../lib/advisor-drawer-analytics";
import { useAdvisorDrawerStore, useAdvisorStore } from "../../stores/advisor";
import { AdvisorDrawerHeader } from "./AdvisorDrawerHeader";
import { useAdvisorSession } from "./AdvisorSessionProvider";
import { AdvisorChatSurface } from "./advisor-chat-surface";

const DEFAULT_EXPAND_HREF = "/dashboard?view=advisor";

/**
 * AdvisorDrawer (plan 435 M6, task asd-drawer).
 *
 * A non-modal Radix dialog docked to the right edge: `min(420px,100vw)` on
 * desktop, a full-height bottom sheet below the `md` breakpoint. Non-modal
 * so the page behind it stays interactive — Ask Sill is a LAYER over the
 * product, not a blocking modal (tech spec, "Ask Sill as a layer").
 *
 * The chat body reuses the SAME presentational `AdvisorChatSurface` the
 * full page renders — the drawer and the full page are two shells over one
 * `useAdvisorSession()`, never two independent chat implementations.
 *
 * SEAM for the integration pass: the `ConsumerFrame` layout route
 * (asd-consumer-frame, out of this lane's scope) is what force-closes the
 * drawer on `?view=advisor` so the two shells are never co-mounted, and
 * what mounts this component once at the frame level (so it shares the
 * SAME `<AdvisorSessionProvider>` as the full page). Mounted standalone
 * today (e.g. in tests or a future preview fixture) it still works
 * correctly via `useAdvisorSession()`'s self-provided fallback — it just
 * won't share a session with anything else on the page.
 *
 * `expandHref` defaults to the real full-page destination; the Clerk-free
 * preview/e2e fixtures (task asd-fixtures-e2e) override it to a Clerk-free
 * fixture route so Expand never navigates somewhere that requires Clerk.
 */
export function AdvisorDrawer({
  expandHref = DEFAULT_EXPAND_HREF,
}: {
  expandHref?: string;
} = {}) {
  const open = useAdvisorDrawerStore((s) => s.drawerOpen);
  const activeContext = useAdvisorDrawerStore((s) => s.activeContext);
  const closeDrawer = useAdvisorDrawerStore((s) => s.closeDrawer);
  const collapseDrawer = useAdvisorDrawerStore((s) => s.collapseDrawer);
  const navigate = useNavigate();
  const session = useAdvisorSession();
  const stopActiveTurn = useAdvisorStore((s) => s.stopActiveTurn);
  const addMessage = useAdvisorStore((s) => s.addMessage);
  const setStreaming = useAdvisorStore((s) => s.setStreaming);
  const { handleCloseAutoFocus, skipNextRestore } = useRestoreFocusOnClose();
  const posthog = usePostHog();
  const capture = useCallback(
    (event: string, properties?: Record<string, unknown>) =>
      posthog?.capture?.(event, properties),
    [posthog],
  );

  // Analytics bookkeeping (task asd-analytics). `didExpandRef` distinguishes
  // an Expand-triggered collapse (its own `advisor_drawer_expanded` event,
  // fired directly in `handleExpand`) from every other dismissal (X, Escape,
  // sign-out) — both transition `open` true→false, but only the latter is a
  // "close". `Date.now()` only ever runs inside the Effect below (never
  // during render, which the `react-hooks/purity` rule forbids) — the FIRST
  // effect run handles the "already open at mount" case (every real entry
  // point opens the store BEFORE `AdvisorDrawer` itself ever renders) by
  // stamping `openedAtRef` without treating it as a false "closed" edge.
  const openedAtRef = useRef<number | null>(null);
  const wasOpenRef = useRef(open);
  const didExpandRef = useRef(false);
  const isFirstEffectRunRef = useRef(true);
  useEffect(() => {
    if (isFirstEffectRunRef.current) {
      isFirstEffectRunRef.current = false;
      if (open) {
        openedAtRef.current = Date.now();
      }
      wasOpenRef.current = open;
      return;
    }
    if (open && !wasOpenRef.current) {
      openedAtRef.current = Date.now();
    }
    if (!open && wasOpenRef.current) {
      if (!didExpandRef.current && openedAtRef.current !== null) {
        const turns = session.messages.filter((m) => m.role === "user").length;
        captureAdvisorDrawerClosed(
          capture,
          turns,
          bucketAdvisorDrawerDuration(openedAtRef.current, Date.now()),
        );
      }
      didExpandRef.current = false;
      openedAtRef.current = null;
    }
    wasOpenRef.current = open;
    // session.messages intentionally excluded — only the LENGTH at the
    // moment of close matters, read fresh inside the branch above, not a
    // reason to re-run this effect on every streamed token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, capture]);

  const handleExpand = useCallback(() => {
    const turns = session.messages.filter((m) => m.role === "user").length;
    captureAdvisorDrawerExpanded(
      capture,
      advisorContextKindOf(activeContext),
      turns,
    );
    didExpandRef.current = true;
    // Expand navigates away immediately — focus belongs to the full-page
    // destination, not back to whatever opened the drawer (focus-return
    // fix, UI review).
    skipNextRestore();
    // `collapseDrawer` — NOT `closeDrawer` — so `activeContext` survives the
    // hand-off: the full page's `AdvisorView` resolves `store.activeContext`
    // first (lib/advisor-context.ts `resolveActiveAdvisorContext`), and a
    // `closeDrawer()` here would null it out before that read ever happens.
    collapseDrawer();
    navigate(expandHref);
  }, [
    activeContext,
    capture,
    collapseDrawer,
    expandHref,
    navigate,
    session.messages,
    skipNextRestore,
  ]);

  // Mirrors the full page's submit (`advisor-live-chat.tsx`): the socket
  // never echoes the user's own turn, so the bubble is added locally and the
  // surface flips to streaming before the frame goes out. Sending the frame
  // alone left typed text AND follow-up chip taps invisible in the drawer.
  const handleSubmit = useCallback(
    (text: string) => {
      if (session.isStreaming) return;
      addMessage({
        id: `user-${Date.now()}`,
        role: "user",
        text,
        createdAt: new Date().toISOString(),
      });
      setStreaming(true);
      session.send({ type: "message", text });
    },
    [addMessage, session, setStreaming],
  );

  const handleStopStreaming = useCallback(() => {
    if (!session.isStreaming) return;
    stopActiveTurn();
    session.send({ type: "cancel" });
  }, [session, stopActiveTurn]);

  // Remount the chat surface's local draft state when the entity context
  // changes so a newly-seeded suggestion (task asd-store-context) actually
  // shows up — `AdvisorChatSurface` only reads `initialDraft` once, at
  // mount, mirroring the full page's `key={...}` idiom.
  const contextKey = activeContext
    ? `${activeContext.kind}:${"id" in activeContext ? activeContext.id : ""}`
    : "none";

  // UI review fix (iteration 2, finding 6): Radix's default open-autofocus
  // lands on the first focusable element in the content — the header's
  // Expand button on desktop, or "New conversation" on mobile (Expand is
  // hidden there) — leaving a visible focus ring on a chrome button
  // instead of the composer. `preventDefault` + focusing the composer
  // textarea directly matches the design's "focus the composer on open".
  //
  // `onCloseAutoFocus` is NOT left at Radix's default (corrected — the
  // prior version of this comment claimed the default "already returns
  // focus to the trigger that opened the drawer", which does not hold
  // here: this drawer opens from external store state with no
  // `DialogPrimitive.Trigger` ever rendered, so Radix's internal trigger
  // ref is permanently null and its default handler's `.focus()` call is
  // a silent no-op — see `useRestoreFocusOnClose` for the real fix and
  // the full explanation of why Radix's default leaves focus on `<body>`.
  const contentRef = useRef<HTMLDivElement>(null);
  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    contentRef.current
      ?.querySelector<HTMLTextAreaElement>('[data-slot="advisor-composer"]')
      ?.focus();
  }, []);

  return (
    <DialogPrimitive.Root
      modal={false}
      onOpenChange={(next) => {
        if (!next) closeDrawer();
      }}
      open={open}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby=""
          // bg-background (not bg-card): the same canvas the full Ask Sill
          // page sits on, so the composer and bubbles read identically.
          className="fixed inset-0 z-50 flex flex-col bg-background shadow-xl outline-none md:inset-y-0 md:left-auto md:right-0 md:w-[min(420px,100vw)] md:border-l md:border-border"
          data-slot="advisor-drawer"
          onCloseAutoFocus={handleCloseAutoFocus}
          onEscapeKeyDown={closeDrawer}
          // Non-modal (`modal={false}`) is "the page behind it stays
          // interactive" (drawer design doc §3) — NOT "any click outside
          // dismisses it", which is Radix's `DismissableLayer` default even
          // for non-modal dialogs. Clicking a nav link, a sidebar item, or
          // anything else on the page behind the drawer must never close
          // it; only the X button, Escape, or starting a new conversation
          // do. (Found via Playwright: a real pointerdown on an outside
          // element closed the drawer; no jsdom-based unit test exercises
          // Radix's real pointer-event dismiss path.)
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={handleOpenAutoFocus}
          ref={contentRef}
        >
          <DialogPrimitive.Title className="sr-only">
            Ask Sill
          </DialogPrimitive.Title>
          <AdvisorDrawerHeader
            isStreaming={session.isStreaming}
            onClose={closeDrawer}
            onExpand={handleExpand}
            onNewConversation={session.startNewConversation}
          />
          {/*
           * `variant="drawer"` (2026-10-08) renders the chat's PHONE layout
           * at every viewport width — the page's own `xl:` breakpoints read
           * the WINDOW, so on desktop the drawer got the wide layout and
           * below `xl:` the page's `fixed` composer escaped the drawer.
           */}
          <div
            className="min-h-0 flex-1"
            data-slot="advisor-drawer-chat-column"
          >
            <AdvisorChatSurface
              initialDraft={
                seedSuggestionForContext(activeContext) ?? undefined
              }
              isStreaming={session.isStreaming}
              key={contextKey}
              messages={session.messages}
              onFollowup={handleSubmit}
              onStopStreaming={handleStopStreaming}
              onSubmitPrompt={handleSubmit}
              reserveMobileTabBarSpace={false}
              suggestions={[]}
              variant="drawer"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
